import { Suspense, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { onAuthStateChange } from "../services/authenticationService";
import { AuthProvider } from "../context/AuthProvider";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ensureRegionInfo } from "../services/regionService";
import { captureFbclidFromUrl } from "../services/fbclidService";
import { notifyAnalyticsContextUpdated } from "../services/loggingService";

export default function Root() {
  /// Navigation
  const location = useLocation();

  /// State
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /// Effects
  useEffect(() => {
    const unsubscribe = onAuthStateChange((authenticated) => {
      setIsAuthenticated(authenticated);
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    void ensureRegionInfo()
      .then(() => {
        notifyAnalyticsContextUpdated();
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const targetId = decodeURIComponent(location.hash.replace("#", ""));
    let attempts = 0;
    const maxAttempts = 20;

    const intervalId = window.setInterval(() => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        window.clearInterval(intervalId);
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, 100);

    return () => window.clearInterval(intervalId);
  }, [location.hash, location.pathname]);

  useEffect(() => {
    captureFbclidFromUrl();
  }, [location.search]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black" />
    );
  }

  /// Helpers
  const isAuthRoute = location.pathname.startsWith("/auth");
  const isDashboardRoute = location.pathname.startsWith("/dashboard");
  const isHomeRoute = location.pathname === "/";
  const isOnboardingRoute =
    isHomeRoute || location.pathname.startsWith("/onboarding");

  // Redirect authenticated users away from auth pages
  // Authenticated users are allowed to stay on "/" (onboarding) now.
  if (isAuthenticated && isAuthRoute) {
    return <Navigate to="/" replace />;
  }

  // Redirect unauthenticated users away from protected routes
  if (!isAuthenticated && isDashboardRoute) {
    return <Navigate to="/" replace />;
  }

  /// Render
  return (
    <AuthProvider>
      <div
        className={`min-h-screen ${
          isOnboardingRoute ? "bg-black p-0" : "p-6"
        }`}
        style={isOnboardingRoute ? undefined : { backgroundColor: "#000000" }}
      >
        {!isOnboardingRoute && !isAuthenticated && (
          <div className="pointer-events-none fixed right-4 top-4 z-50">
            <LanguageSwitcher className="pointer-events-auto" />
          </div>
        )}
        <Suspense
          fallback={null}
        >
          <Outlet />
        </Suspense>
      </div>
    </AuthProvider>
  );
}

